import { useEffect, useRef } from 'react';

export function useEnterSubmit({
  onSubmit,
  isPending = false,
  disabled = false,
}) {
  const formRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        if (isPending || disabled) return;

        const target = e.target;
        if (!target) return;

        // Skip if target is textarea
        if (target.tagName === 'TEXTAREA') return;

        // Look for common custom dropdowns, date pickers, or tag inputs that handle their own Enter
        const closestExclusion = target.closest(
          '[role="combobox"], [role="listbox"], [role="menu"], .Select, .react-select, .custom-select, .tag-input, .date-picker',
        );
        if (closestExclusion) return;

        // If the focused element is within our form wrapper
        if (formRef.current && formRef.current.contains(target)) {
          // If the target is a button, don't double submit (native browser behavior will handle it)
          if (target.tagName === 'BUTTON' || target.type === 'submit') {
            return;
          }

          e.preventDefault(); // Prevent default form submission or other unwanted side effects

          // Submit the form!
          if (onSubmit) {
            onSubmit();
          } else {
            // Fallback for native forms if no explicit onSubmit is provided
            const formElement = target.closest('form');
            if (formElement && formElement === formRef.current) {
              formElement.requestSubmit();
            }
          }
        }
      }
    };

    const element = formRef.current;
    if (element) {
      element.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      if (element) {
        element.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [onSubmit, isPending, disabled]);

  return formRef;
}
